import { Component, inject, OnInit, signal } from '@angular/core';
import { CurrentUserService } from '../../../../common/current-user/current-user.service';
import { environment } from '../../../../../environments/environment';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { ChoosePlanService } from '../../../choose-plan/service/choose-plan/choose-plan.service';
import { AlertService } from '../../../../atoms/alert/alert.service';
import { firstValueFrom } from 'rxjs';
import { SharePaymentLink } from '../share-payment-link/share-payment-link';
import { ContactSupportModal } from '../contact-support-modal/contact-support-modal';
import { SubscriptionService } from '../../../../services/subscription/subscription.service';
import { Router } from '@angular/router';

@Component({
  selector: 'mb-brand-billing',
  standalone: true,
  imports: [CurrencyPipe, DatePipe, SharePaymentLink, ContactSupportModal],
  templateUrl: './brand-billing.component.html',
  styleUrl: './brand-billing.component.css'
})
export class BrandBillingComponent implements OnInit {


  openingBillingPortal = signal<boolean>(false);
  showSharePaymentLinkModal = signal<boolean>(false);
  showContactSupportModal = signal<boolean>(false);
  paymentLink = signal<string | undefined>(undefined);
  removingBoltOnId = signal<string | null>(null);

  /** Services */
  public currentUserService = inject(CurrentUserService);
  private choosePlanService = inject(ChoosePlanService);
  private readonly router = inject(Router);
  private alertService = inject(AlertService);
  private subscriptionService = inject(SubscriptionService);
  /** Services - END */


  ngOnInit(): void {
    this.warmPaymentLinks();

    if (this.currentUserService.currentSubscription()) {
      console.log('🤑🤑🤑 currentUserService.currentSubscription()', this.currentUserService.currentSubscription().items.data);
    } else {
      console.log('🤑🤑🤑 currentUserService.currentSubscription() is undefined');
    }
  }

  warmPaymentLinks(): void {
    const customerIdentifiers = this.getStripeCustomerIdentifiers();
    for (const identifier of customerIdentifiers) {
      void this.currentUserService.prefetchApprovalLink(identifier, 7);
    }
  }

  getProductName(productId: string) {
    if (productId == environment.stripe.shaas21products.sub_essential) {
      return 'Essential Plan';
    } else if (productId == environment.stripe.products.essentials) {
      return 'Original Essentials Plan';
    } else if (productId == environment.stripe.shaas21products.bo_ai_moderation) {
      return 'AI Video Moderation';
    } else if (productId == environment.stripe.shaas21products.bo_gifting_seeding) {
      return 'Gifting Seeding';
    } else if (productId == environment.stripe.shaas21products.bo_affiliates_shopify) {
      return 'Affiliates Shopify';
    } else if (productId == environment.stripe.shaas21products.bo_affiliates_rakuten) {
      return 'Affiliates Rakuten';
    } else if (productId == environment.stripe.shaas21products.bo_monitoring) {
      return 'Social Monitoring';
    }
    else {
      return 'Name...'
    }
  }

  hideBaseSub(boltOn: any) {
    return boltOn.price.product.id == environment.stripe.shaas21products.sub_essential;
  }

  getBoltOnPrice(boltOn: { quantity?: number; price?: { unit_amount?: number } }) {
    return this.lineItemAmount(boltOn);
  }

  private lineItemQuantity(item: { quantity?: number } | null | undefined): number {
    const quantity = item?.quantity;
    return typeof quantity === 'number' && quantity > 0 ? quantity : 1;
  }

  private lineItemAmount(item: { quantity?: number; price?: { unit_amount?: number } }): number {
    return ((item.price?.unit_amount ?? 0) / 100) * this.lineItemQuantity(item);
  }

  getBillingPeriod(boltOn: any) {
    return boltOn.price.recurring.interval == 'month' ? 'Monthly' : 'Yearly';
  }

  async removeBoltOn(boltOn: any): Promise<void> {
    if (this.hideBaseSub(boltOn)) return;

    const productName = this.getProductName(boltOn.plan.product);

    const confirmed = await this.alertService.confirm({
      title: 'Remove bolt-on',
      message: `Are you sure you want to remove "${productName}"? You'll be credited for any unused time.`,
      confirmText: 'Remove',
      cancelText: 'Cancel',
      type: 'confirm',
      icon: 'warning',
    });

    if (!confirmed) return;

    const customerId = this.currentUserService.focussedBrand?.stripeCustomerId;
    if (!customerId) return;

    this.removingBoltOnId.set(boltOn.id);
    try {
      const updatedSubscription = await firstValueFrom(
        this.subscriptionService.removeBoltOnLineItem(customerId, boltOn.id)
      );
      this.currentUserService.setCurrentSubscription(updatedSubscription);
    } catch (error) {
      console.error('Failed to remove bolt-on', error);
      await this.alertService.alert({
        title: 'Could not remove bolt-on',
        message: 'Something went wrong. Please try again or contact support.',
        icon: 'info',
        type: 'alert',
        confirmText: 'OK',
        cancelText: 'Close',
      });
    } finally {
      this.removingBoltOnId.set(null);
    }
  }

  convertDate(date: number) {
    return new Date(date * 1000);
  }

  isGbpBilling(): boolean {
    return this.currentUserService.currentSubscription()?.currency?.toUpperCase() === 'GBP';
  }

  getBaseSubPrice() {
    let baseSubPrice = 0;
    for (const item of this.currentUserService.currentSubscription().items.data) {
      if (item.price.product.id == environment.stripe.shaas21products.sub_essential || item.price.product.id == environment.stripe.products.essentials) {
        baseSubPrice = this.lineItemAmount(item);
        break;
      }
    }
    return baseSubPrice;
  }

  getTotalPrice() {
    let totalPrice = 0;
    for (const item of this.currentUserService.currentSubscription().items.data) {
      totalPrice += this.lineItemAmount(item);
    }
    return totalPrice;
  }

  getActiveBoltOnCount(): number {
    const items = this.currentUserService.currentSubscription()?.items?.data ?? [];
    return items
      .filter((item: { price?: { product?: { id?: string } } }) => !this.hideBaseSub(item))
      .reduce((sum: number, item: { quantity?: number }) => sum + this.lineItemQuantity(item), 0);
  }

  boltOnDisplayName(boltOn: { plan?: { product?: string }; quantity?: number }): string {
    const name = this.getProductName(boltOn.plan?.product ?? '');
    const quantity = this.lineItemQuantity(boltOn);
    return quantity > 1 ? `${name} (${quantity})` : name;
  }

  getStatusClass(status: string) {
    switch (status) {
      case 'active':
        return 'text-green-500';
      case 'trialing':
        return 'text-emerald-500';
      case 'canceled':
        return 'text-red-500';
      case 'past_due':
        return 'text-red-500';
      default:
        return 'text-gray-500';
    }
  }

  subscribe() {
    this.choosePlanService.launchSubscribeOrBoltOn();
  }

  /**
   * Opens the off-boarding flow instead of dumping the customer in the Stripe
   * billing portal.
   *
   * The portal cancels silently: we learn nothing about why they left, and the
   * customer is told nothing about the creators they have already hired and
   * still owe. The flow exists to fix both.
   */
  async startOffboarding(): Promise<void> {
    await this.router.navigate(['/cancel-subscription']);
  }

  async goToStripe() {
    console.log('🤑🤑🤑 goToStripe() 🤑🤑🤑');

    // window.open(environment.stripe.managmentUrl, "_blank");

    if (this.openingBillingPortal()) {
      return;
    }

    const customerIdentifiers = this.getStripeCustomerIdentifiers();
    if (!customerIdentifiers.length) {
      // this.showChoosePlanPrompt('Add payment method');
      return;
    }

    try {
      this.openingBillingPortal.set(true);
      // `/account/3` was a shaas18 route and does not exist in this app, so
      // returning from Stripe used to land on the 404 fallback.
      const returnUrl = `${window.location.protocol}//${window.location.host}/my-brand/billing`;
      const billingUrl = await this.getBillingPortalUrlForAnyIdentifier(customerIdentifiers, returnUrl);
      if (billingUrl) {
        window.open(billingUrl, "_blank");
        return;
      }

      const fallbackUrl = await this.getFallbackApprovalUrl(customerIdentifiers);
      if (fallbackUrl) {
        window.open(fallbackUrl, "_blank");
        return;
      }

      this.handleBillingPortalUnavailable();
    } catch (error) {
      console.error('Failed to open Stripe billing portal', error);
      try {
        const fallbackUrl = await this.getFallbackApprovalUrl(customerIdentifiers);
        if (fallbackUrl) {
          window.open(fallbackUrl, "_blank");
          return;
        }
      } catch (fallbackError) {
        console.error('Fallback billing URL lookup failed', fallbackError);
      }

      this.handleBillingPortalUnavailable();
    } finally {
      this.openingBillingPortal.set(false);
    }
  }

  private getStripeCustomerIdentifiers(): string[] {
    const subscription = this.currentUserService.currentSubscription();
    const subscriptionCustomer =
      typeof subscription?.customer === 'string'
        ? subscription.customer
        : subscription?.customer?.id;

    const candidates = [
      this.currentUserService.focussedBrand?.stripeCustomerId ||
      subscriptionCustomer
    ];

    return Array.from(new Set(candidates.filter((value): value is string => !!value && value.length > 0)));
  }

  private async handleBillingPortalUnavailable(): Promise<void> {
    if (this.isTrialingSubscription()) {
      await this.currentUserService.refreshFocussedSubscription();
      await this.alertService.alert({
        title: 'Billing setup in progress',
        message: 'Your Essentials trial is active. Stripe billing setup may still be syncing. Please try again in a moment.',
        icon: 'info',
        type: 'alert',
        confirmText: 'OK',
        cancelText: 'Cancel'
      });

      return;
    }

    await this.alertService.alert({
      title: 'Could not open Stripe billing',
      message: 'Please try again in a moment.',
      icon: 'info',
      type: 'alert',
      confirmText: 'OK',
      cancelText: 'Close'
    });
  }

  isTrialingSubscription(): boolean {
    const subscriptionStatus = this.currentUserService.currentSubscription()?.status;
    if (subscriptionStatus === 'trialing') {
      return true;
    }

    if (subscriptionStatus && subscriptionStatus !== 'incomplete') {
      return false;
    }

    const trialEnd = this.currentUserService.getTrialEndDate(this.currentUserService.currentSubscription());
    return !!trialEnd && trialEnd.getTime() > Date.now();
  }

  private async getFallbackApprovalUrl(customerIdentifiers: string[]): Promise<string | null> {
    for (const identifier of customerIdentifiers) {
      try {
        const cached = this.currentUserService.getCachedApprovalLink(identifier);
        if (cached) {
          return cached;
        }
        return await this.currentUserService.prefetchApprovalLink(identifier, 7);
      } catch (error) {
        console.warn('Approval link lookup failed for identifier', identifier, error);
      }
    }

    return null;
  }

  private async getBillingPortalUrlForAnyIdentifier(customerIdentifiers: string[], returnUrl: string): Promise<string | null> {
    for (const identifier of customerIdentifiers) {
      try {
        const session: any = await firstValueFrom(this.currentUserService.createBillingPortalSession(identifier, returnUrl));
        if (session?.url) {
          return session.url;
        }
      } catch (error) {
        console.warn('Billing portal lookup failed for identifier', identifier, error);
      }
    }

    return null;
  }

  openContactSupport(): void {
    void this.router.navigate(['/support/new']);
  }

  async sendPaymentToTeam() {
    console.log('🤑🤑🤑 sendPaymentToTeam() 🤑🤑🤑');

    this.showSharePaymentLinkModal.set(true);

    const customerIdentifiers = this.getStripeCustomerIdentifiers();
    const paymentUrl = await this.getFallbackApprovalUrl(customerIdentifiers);

    if (paymentUrl) {
      this.paymentLink.set(paymentUrl);
    } else {
      this.paymentLink.set(undefined);
      this.showSharePaymentLinkModal.set(false);
    }
  }

}
